<template>
  <div>
    <div class="flex align-items-center justify-end mb-8 mr-8">
      <span v-if="searching" class="text-bold text-danger">{{ searching }}</span>
      <v-select v-model="merchantID" :options="merchants" :reduce="merchant => merchant.ID" class="ml-8"
                label="Name" placeholder="Select Owner" style="min-width: 200px;"></v-select>
      <input v-model="keyword" aria-label="Search" class="form-control ml-8" placeholder="Search..."
             type="text" style="width: 200px" @keyup="searchCompletedPayouts()"/>
      <router-link v-if="merchantID" :to="{ name: 'payouts' }"
                   class="btn btn-default btn-flat btn-sm ml-8" tag="a">Back
      </router-link>
    </div>
    <div class="table-responsive">
      <table class="table table-condensed table-striped">
        <thead>
        <tr>
          <th></th>
          <th class="pointer" @click="sort('ID')">
            Payout ID
            <span v-if="sortKey === 'ID'" class="ml-4">
              <i class="fa fa-sort"></i>
            </span>
          </th>
          <th class="pointer" @click="sort('MerchantID')">
            Merchant ID
            <span v-if="sortKey === 'MerchantID'" class="ml-4">
              <i class="fa fa-sort"></i>
            </span>
          </th>
          <th class="pointer" @click="sort('OwnerNickName')">
            Owner
            <span v-if="sortKey === 'OwnerNickName'" class="ml-4">
              <i class="fa fa-sort"></i>
            </span>
          </th>
          <th class="no-wrap pointer" @click="sort('ShopTitle')">
            Shop
            <span v-if="sortKey === 'ShopTitle'" class="ml-4">
              <i class="fa fa-sort"></i>
            </span>
          </th>
          <th class="pointer" @click="sort('FinalVerification')">
            Merchant Status
            <span v-if="sortKey === 'FinalVerification'" class="ml-4">
              <i class="fa fa-sort"></i>
            </span>
          </th>
          <th class="pointer" @click="sort('PayoutSchema')">
            Payout Schema Used
            <span v-if="sortKey === 'PayoutSchema'" class="ml-4">
              <i class="fa fa-sort"></i>
            </span>
          </th>
          <th class="pointer" @click="sort('PayoutTypeName')">
            Payout Type
            <span v-if="sortKey === 'PayoutTypeName'" class="ml-4">
              <i class="fa fa-sort"></i>
            </span>
          </th>
          <th>Crypto Name</th>
          <th class="pointer" @click="sort('Amount')">
            Amount
            <span v-if="sortKey === 'Amount'" class="ml-4">
              <i class="fa fa-sort"></i>
            </span>
          </th>
          <th class="pointer" @click="sort('CreatedOn')">
            Created On
            <span v-if="sortKey === 'CreatedOn'" class="ml-4">
              <i class="fa fa-sort"></i>
            </span>
          </th>
          <th class="pointer" @click="sort('UpdateOn')">
            Update On
            <span v-if="sortKey === 'UpdateOn'" class="ml-4">
              <i class="fa fa-sort"></i>
            </span>
          </th>
          <th class="pr-16">Actions</th>
        </tr>
        </thead>
        <tbody>
        <template v-if="payouts.length > 0">
          <template v-for="(payout, index) in payouts">
            <tr :key="index">
              <td>
                <span class="flex pointer text-success" @click="toggleRow(index)">
                  <svg v-if="expandedRowIndex !== index + 1" class="w-24 h-24" fill="none" stroke="currentColor"
                       viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
                    <path d="M12 9v3m0 0v3m0-3h3m-3 0H9m12 0a9 9 0 11-18 0 9 9 0 0118 0z"
                          stroke-linecap="round" stroke-linejoin="round" stroke-width="2"></path>
                  </svg>
                  <svg v-if="expandedRowIndex === index + 1" class="w-24 h-24" fill="none" stroke="currentColor"
                       viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
                    <path d="M15 12H9m12 0a9 9 0 11-18 0 9 9 0 0118 0z"
                          stroke-linecap="round" stroke-linejoin="round" stroke-width="2"></path>
                  </svg>
                </span>
              </td>
              <td>
                <span class="flex align-items-center">
                  <router-link
                    :to="{name: 'transaction-detail', params:{ id: 'P' + payout.ID.toString().padStart(9, '0') }}"
                    tag="a">{{ payout.ID }}
                  </router-link>
                  <span class="ml-8">
                    <i class="fa fa-copy pointer" @click="copyToClipboard(payout.ID.toString())"></i>
                  </span>
                </span>
              </td>
              <td>
                <router-link :to="{ name: 'payouts', params: { id: payout.MerchantID } }" tag="a">
                  {{ payout.MerchantID }}
                </router-link>
              </td>
              <td class="no-wrap">
                <router-link :to="{ name: 'user-detail', params: { id: payout.MerchantID } }" tag="a">
                  {{ payout.OwnerNickName }}
                </router-link>
              </td>
              <td class="no-wrap">
                <router-link :to="{ name: 'user-detail', params: { id: payout.MerchantID } }" tag="a">
                  {{ payout.ShopTitle }}
                </router-link>
              </td>
              <td v-html="formatMerchantStatus(payout.FinalVerification)"></td>
              <td>{{ isPayoutType(payout) }}</td>
              <td>
                <span @click="launchUpdateTransactionLogsModal(payout, index)"
                  class="pointer">{{ payout.PayoutTypeName }}</span>
              </td>
              <td>{{ payout.CoinName }}</td>
              <td class="no-wrap">
                <span class="flex align-items-center">
                  <span class="mr-8">
                    <i class="fa fa-copy pointer" @click="toConverter(toFixed(payout.Balance), payout.AltCoinID)"></i>
                  </span>
                  <router-link tag="a" :to="{ name: 'payouts', query: { id: payout.AltCoinID } }">
                    {{ toFixed(payout.Balance) + ' ' + payout.LongName }}
                  </router-link>
                </span>
              </td>
              <td class="no-wrap">{{ formatDate(payout.CreatedOn) }}</td>
              <td class="no-wrap">{{ formatDate(payout.UpdateOn) }}</td>
              <td class="flex align-items-center">
                <button v-tooltip="{ content: 'Reverse the Payout balance', trigger: 'click hover focus' }" v-if="payout.Balance > 0" class="btn btn-primary btn-sm btn-flat mr-8"
                        @click="reversePayout(payout)">
                  Reverse Payout
                </button>
                <button v-else disabled class="btn btn-primary btn-sm btn-flat mr-8">
                  Zero Amount
                </button>
                <button v-tooltip="{ content: 'Retrigger the payout to pending', trigger: 'click hover focus' }" v-if="payout.Balance > 0" class="btn btn-danger btn-flat btn-sm mr-8" @click="manualPaid(payout, index)">Reinitiate
                </button>
              </td>
            </tr>
            <template v-if="expandedRowIndex === (index + 1)">
              <tr :key="'detail_' + index">
                <td class="px-15" colspan="15">
                  <div class="row">
                    <div class="col-md-6">
                      <table class="table table-condensed table-striped mb-0">
                        <tbody>
                        <tr>
                          <th colspan="2">Payout Information</th>
                        </tr>
                        <template v-if="isPayoutType(payout) === 'Bank'">
                          <tr v-if="payout.PayoutName">
                            <td>Account Holder Name</td>
                            <td>{{ payout.PayoutName }}</td>
                          </tr>
                          <tr v-if="payout.PayoutIBAN">
                            <td>IBAN/Account Number</td>
                            <td>{{ payout.PayoutIBAN }}</td>
                          </tr>
                          <tr v-if="payout.AccountType">
                            <td>Account Type</td>
                            <td>{{ payout.AccountType.toLowerCase() }}</td>
                          </tr>
                          <tr v-if="payout.TaxId">
                            <td>Tax Id: CUIL / CUIT</td>
                            <td>{{ payout.TaxId }}</td>
                          </tr>
                          <tr v-if="payout.BsbCode">
                            <td>BSC Code</td>
                            <td>{{ payout.BsbCode }}</td>
                          </tr>
                          <tr v-if="payout.PayoutBIC">
                            <td>BIC/Swift/Routing code</td>
                            <td>{{ payout.PayoutBIC }}</td>
                          </tr>
                          <tr v-if="payout.BranchCode">
                            <td>Branch Code</td>
                            <td>{{ payout.BranchCode }}</td>
                          </tr>
                          <tr v-if="payout.CPF">
                            <td>Recipient tax ID/CPF</td>
                            <td>{{ payout.CPF }}</td>
                          </tr>
                          <tr v-if="payout.TransitNumber">
                            <td>Transit Number</td>
                            <td>
                              {{ payout.TransitNumber }}
                            </td>
                          </tr>
                          <tr v-if="payout.PhoneNumber">
                            <td>Account Number</td>
                            <td>{{ payout.PhoneNumber }}</td>
                          </tr>
                          <tr v-if="payout.InstitutionNumber">
                            <td>Institution Number</td>
                            <td>{{ payout.InstitutionNumber }}</td>
                          </tr>
                          <tr v-if="payout.TransitCode">
                            <td>Transit Number</td>
                            <td>{{ payout.TransitCode }}</td>
                          </tr>
                          <tr v-if="payout.RUT">
                            <td>Beneficiary's RUT number (Rol Único Tributario)</td>
                            <td>{{ payout.RUT }}</td>
                          </tr>
                          <tr v-if="payout.CardNumber">
                            <td class="no-wrap">UnionPay card number</td>
                            <td>{{ payout.CardNumber }}</td>
                          </tr>
                          <tr v-if="payout.SortCode">
                            <td class="no-wrap">Sort Code</td>
                            <td>{{ payout.SortCode }}</td>
                          </tr>
                          <tr v-if="payout.IfscCode">
                            <td class="no-wrap">IFSC Code</td>
                            <td>{{ payout.IfscCode }}</td>
                          </tr>
                          <tr v-if="payout.Clabe">
                            <td class="no-wrap">Recipient CLABE Number</td>
                            <td>{{ payout.Clabe }}</td>
                          </tr>
                          <tr v-if="payout.SwiftCode">
                            <td class="no-wrap">Swift Code</td>
                            <td>{{ payout.SwiftCode }}</td>
                          </tr>
                          <tr v-if="payout.IdDocumentType">
                            <td class="no-wrap">ID Document Type</td>
                            <td>{{ payout.IdDocumentType }}</td>
                          </tr>
                          <tr v-if="payout.IdDocumentNumber">
                            <td class="no-wrap">Identification Number</td>
                            <td>{{ payout.IdDocumentNumber }}</td>
                          </tr>
                          <tr v-if="payout.Abartn">
                            <td class="no-wrap">Routing Number</td>
                            <td>{{ payout.Abartn }}</td>
                          </tr>
                          <tr v-if="payout.FirstLine">
                            <td>Address</td>
                            <td>{{ payout.FirstLine }}</td>
                          </tr>
                          <tr v-if="payout.City">
                            <td>City</td>
                            <td>{{ payout.City }}</td>
                          </tr>
                          <tr v-if="payout.State">
                            <td>State</td>
                            <td>{{ payout.State }}</td>
                          </tr>
                          <tr v-if="payout.PostCode">
                            <td>Postal Code</td>
                            <td>{{ payout.PostCode }}</td>
                          </tr>
                          <tr v-if="payout.Country">
                            <td>Country</td>
                            <td>{{ payout.Country }}</td>
                          </tr>
                        </template>
                        <template v-if="isPayoutType(payout) === 'Payeer'">
                          <tr v-if="payout.PayoutTo">
                            <td>Payeer</td>
                            <td>{{ payout.PayoutTo }}</td>
                          </tr>
                        </template>
                        <template v-if="isPayoutType(payout) === 'PayPal'">
                          <tr v-if="payout.PayoutTo">
                            <td>PayPal</td>
                            <td>{{ payout.PayoutTo }}</td>
                          </tr>
                        </template>
                        <template v-if="isPayoutType(payout) === 'Crypto'">
                          <tr>
                            <td class="no-wrap">Crypto Currency</td>
                            <td>{{ payout.CoinName + ' - ' + payout.LongName }}</td>
                          </tr>
                          <tr>
                            <td class="no-wrap">Address</td>
                            <td>
                              <span style="word-break: break-all;">{{ payout.CoinAddressUsed }}</span>
                            </td>
                          </tr>
                        </template>
                        <tr>
                          <td class="no-wrap">Amount Fiat Value</td>
                          <td>{{ toFixed(payout.AmountFiatValue) + ' EUR' }}</td>
                        </tr>
                        <tr>
                          <td>Target Currency</td>
                          <td>{{ payout.TargetCurrency }}</td>
                        </tr>
                        <tr>
                          <td>Payout Type</td>
                          <td>{{ payout.PayoutTypeName }}</td>
                        </tr>
                        </tbody>
                      </table>
                    </div>
                    <div class="col-md-6">
                      <a
                        :href="'https://cointopay.com/CsvExportAdmin.jsp?type=1&AltCoinID=' + payout.AltCoinID + '&MerchantID=' + payout.MerchantID"
                        class="mr-8" target="_blank">
                        <img alt="" class="w-48 h-48" src="/assets/img/csv.svg"/>
                      </a>
                      <a
                        :href="'https://cointopay.com/CsvExportAdmin.jsp?type=2&AltCoinID=' + payout.AltCoinID + '&MerchantID=' + payout.MerchantID"
                        target="_blank">
                        <img alt="" class="w-48 h-48" src="/assets/img/csv.svg"/>
                      </a>
                    </div>
                  </div>
                </td>
              </tr>
            </template>
          </template>
        </template>
        <template v-else>
          <tr>
            <td colspan="15">No record found!</td>
          </tr>
        </template>
        </tbody>
      </table>
    </div>
    <div v-if="payouts && payouts.length" class="footer">
      <pagination :meta="meta" @pageChange="updatePage"></pagination>
    </div>
  </div>
</template>

<script>
import { mapGetters } from 'vuex'
import {
  formatDate,
  formatMerchantStatus,
  formatStatus,
  getPayoutSchemaType,
  logout,
  payoutSchemas,
  toFixed,
  saveNotification
} from '@/lib/utils'
import Pagination from '@/components/Pagination'
import { SAVE_CONVERTER } from '../store/keys'

export default {
  name: 'CompletedPayouts',
  components: {
    Pagination
  },
  props: {
    payouts: {
      type: Array,
      default() {
        return []
      }
    },
    merchants: {
      type: Array,
      default() {
        return []
      }
    },
    meta: {
      type: Object,
      default() {
        return {
          total: 0,
          page: 1,
          offset: 0,
          limit: Number.parseInt(process.env.VUE_APP_LIMIT, 10)
        }
      }
    },
    searchKeyword: {
      type: String,
      default: ''
    },
    searchOrder: {
      type: String,
      default: 'DESC'
    },
    searchSortKey: {
      type: String,
      default: 'UpdateOn'
    }
  },
  data() {
    return {
      payoutSchemas: payoutSchemas,
      merchantID: '',
      keyword: '',
      searching: '',
      timer: null,
      expandedRowIndex: 0,
      sortKey: 'UpdateOn',
      order: 'desc'
    }
  },
  computed: {
    ...mapGetters([
      'currentUser',
      'jwtToken',
      'converter'
    ])
  },
  watch: {
    payouts(value) {
      this.searching = ''
    },
    merchantID(val) {
      if (val === null) {
        this.merchantID = ''
      }
      this.searchCompletedPayouts()
    }
  },
  methods: {
    toConverter(amount, altCoinID) {
      const form = { ...this.converter }
      form.source = amount
      form.sourceAltCoinId = altCoinID
      this.$store.commit(SAVE_CONVERTER, form)
      this.copyToClipboard(amount)
    },
    copyToClipboard(val) {
      const self = this
      navigator.clipboard.writeText(val).then(function() {
        self.$toast.fire('', 'Copied to clipboard: ' + val, 'success')
        saveNotification('Copied to clipboard: ' + val)
      }, function(err) {
        console.error('Async: Could not copy text: ', err)
      })
    },
    sort(key) {
      if (key !== this.sortKey) {
        this.order = 'desc'
      } else {
        this.order = this.order === 'asc' ? 'desc' : 'asc'
      }
      this.sortKey = key
      this.searchCompletedPayouts()
    },
    toFixed(val) {
      return toFixed(val)
    },
    formatStatus(status, maxWidth) {
      return formatStatus(status, maxWidth)
    },
    formatMerchantStatus(status) {
      return formatMerchantStatus(status)
    },
    formatDate(val) {
      return formatDate(val)
    },
    updatePage(page) {
      this.$emit('updatePage', {
        page: page,
        merchantID: this.merchantID,
        keyword: this.keyword ? this.keyword.trim() : ''
      })
    },
    toggleRow(i) {
      i++
      this.expandedRowIndex = this.expandedRowIndex === i ? 0 : i
    },
    searchCompletedPayouts() {
      this.searching = 'Searching...'
      if (this.timer) {
        clearTimeout(this.timer)
      }
      this.timer = setTimeout(() => {
        this.$emit('search', {
          keyword: this.keyword,
          order: this.order,
          sortKey: this.sortKey,
          merchantID: this.merchantID
        })
      }, 500)
    },
    payoutSchemaToSlug(schema) {
      const payoutSchema = this._.find(this.payoutSchemas, { title: schema })
      if (payoutSchema) {
        schema = payoutSchema.slug
      }
      return schema
    },
    getPayoutSchemaType(payoutSchema) {
      return getPayoutSchemaType(payoutSchema)
    },
    manualPaid(payout, index) {
      this.$bus.$emit('update-transaction-logs-modal', {
        title: 'Reinitiate Update Transaction Logs',
        data: {
          TransactionID: payout.ID,
          Prefix: 'P',
          TransactionType: payout.PayoutTypeName,
          ExternalTransactionProvider: '',
          Comments: payout.Comments,
          MerchantID: this.currentUser.ID
        },
        confirm: (comments, paymentProviderID) => {
          const data = this.payoutTypes
          const pp = this._.find(data, { ID: paymentProviderID })
          if (pp) {
            const index = this._.findIndex(this.payouts, { ID: payout.ID })
            if (index !== -1) {
              payout = Object.assign({}, payout, {
                PayoutTypeName: pp.Name,
                PayoutType: pp.ID.toString(),
                Comments: comments
              })
              this.payouts.splice(index, 1, payout)
            }
          }
          this.startLoader()
          const apiBaseUrl = process.env.VUE_APP_API_URL
          this.$http.get(`${apiBaseUrl}/v2REAPI`, {
            params: {
              Call: 'FiatPayoutStatusUpdater',
              MerchantID: 1,
              payoutid: payout.ID,
              APIKey: '_',
              output: 'json'
            }
          }).then(response => {
            this.stopLoader()
            if (response.data.status_code === 200) {
              this.updatePage(this.meta.page)
              setTimeout(() => {
                this.$toast.fire('', response.data.message, 'success')
                saveNotification(response.data.message)
              }, 500)
            } else {
              this.$toast.fire('', response.data.message, 'error')
              if (response.data.message.toLowerCase() === 'no merchant session') {
                logout()
              }
            }
          }).catch(error => {
            this.stopLoader()
            console.log(error)
          })
        }
      })
    },
    startLoader() {
      this.$swal.fire({
        title: '',
        text: 'Please wait...',
        showConfirmButton: false,
        backdrop: true
      })
    },
    stopLoader() {
      this.$swal.close()
    },
    reversePayout(record) {
      this.$swal.fire({
        title: 'Reverse payout',
        text: 'Are you sure you want to reverse the payout?',
        icon: 'warning',
        showCancelButton: true,
        confirmButtonColor: '#00a65a',
        confirmButtonText: 'Confirm',
        cancelButtonText: 'Cancel',
        focusCancel: true
      }).then(({ value }) => {
        if (value && value === true) {
          const apiBaseUrl = process.env.VUE_APP_API_URL
          this.$swal.fire({
            title: '',
            text: 'Please wait...',
            showConfirmButton: false,
            backdrop: true
          })
          const payload = {
            Call: 'ReversePayout',
            MerchantID: this.currentUser.ID,
            APIKey: this.currentUser.APIKey,
            targetMerchant: record.MerchantID,
            PayoutID: record.ID,
            output: 'json'
          }
          this.$http.get(`${apiBaseUrl}/v2REAPI`, {
            params: payload,
            withCredentials: true
          }).then(response => {
            this.$swal.close()
            if (response.data.status_code === 200) {
              if (response.data.result.toLowerCase() === 'success') {
                this.$toast.fire('', 'Payout reverse request submitted successfully', 'success')
                saveNotification('Payout reverse request submitted successfully')
              }
            } else if (response.data.status_code === 400) {
              if (response.data.message.toLowerCase() === 'no permission') {
                logout()
              }
            }
          }).catch(error => {
            this.$swal.close()
            console.log(error)
          })
        }
      })
    },
    isPayoutType(payout) {
      const bank = [2, 5, 10, 12, 16]
      let type = 'Crypto'
      const payoutType = Number.parseInt(payout.PayoutType)
      if (payoutType === 11) {
        type = 'PayPal'
      } else if (payoutType === 17) {
        type = 'Payeer'
      } else if (bank.indexOf(payoutType) !== -1) {
        type = 'Bank'
      }
      return type
    },
    launchUpdateTransactionLogsModal(payout, index) {
      this.$bus.$emit('update-transaction-logs-modal', {
        title: 'Update Transaction Logs',
        data: {
          TransactionID: payout.ID,
          Prefix: 'P',
          TransactionType: payout.PayoutTypeName,
          ExternalTransactionProvider: '',
          Comments: payout.Comments,
          MerchantID: this.currentUser.ID
        },
        confirm: (comments, paymentProviderID) => {
          this.updatePage(this.meta.page)
        }
      })
    }
  },
  mounted() {
    if (this.$route.params.id) {
      this.merchantID = this.$route.params.id
    } else {
      this.merchantID = ''
    }
    this.keyword = this.searchKeyword ? this.searchKeyword.trim() : ''
    this.order = this.searchOrder
    this.sortKey = this.searchSortKey
  }
}
</script>

<style scoped></style>
